/**
 * User API Service
 * User-related API operations following FRONTEND_API_GUIDE
 */

import { apiService } from '@/shared/services';
import { API_ENDPOINTS } from '@/shared/constants';
import type { ApiResponse, ChangePasswordRequest } from '@/shared/types';

// Standard API Response format
export interface StandardResponse<T = unknown> {
  success: boolean;
  message?: string;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
  timestamp?: string;
}

// Auth response data type (what's inside the data field)
export interface AuthResponseData {
  expires_at: string;
  token: string;
}

// Registration response data type (what's inside the data field)
export interface RegistrationResponseData {
  user_id: string;
}

// Login response type - apiService wraps it in ApiResponse, so we just need the data type
export type LoginResponse = AuthResponseData;

// Registration response type - apiService wraps it in ApiResponse, so we just need the data type
export type RegistrationResponse = RegistrationResponseData;

// Session verification response
export interface SessionVerifyResponse {
  username: string;
  email: string;
  project_id: string;
  is_active: boolean;
  session_expires: string;
  is_admin?: boolean;
  is_god?: boolean;
}

// User profile data type
export interface UserProfileData {
  username: string;
  email: string;
  project_id: string;
  is_active: boolean;
  first_name?: string;
  last_name?: string;
  bio?: string;
  profile_image_url?: string;
  created_at?: string;
  updated_at?: string;
  is_admin?: boolean;
  is_god?: boolean;
  data: Record<string, unknown>;
}

// User profile response type
export type UserProfileResponse = StandardResponse<UserProfileData>;

// User update data type
export interface UpdateUserData {
  updated_at: string;
}

// User update response type
export type UpdateUserResponse = StandardResponse<UpdateUserData>;

// User settings data type
export interface UserSettingsData {
  settings: {
    // Nested under `appearance`, matching the backend's real
    // UserSettings.to_dict() shape ({email_preferences, privacy, appearance,
    // notifications}) - this used to be declared flat (`theme` at the top
    // level), which meant every read against it silently found nothing.
    appearance?: {
      theme?: string;
      variant?: string;
      language?: string;
      timezone?: string;
    };
    notifications: {
      email: boolean;
      push: boolean;
      comment_replies: boolean;
    };
    privacy: {
      profile_public: boolean;
      show_activity: boolean;
    };
  };
}

// User settings response type
export type UserSettingsResponse = StandardResponse<UserSettingsData>;

// PUT /users/settings request shape - NOT the same as UserSettingsData
// above: the backend's update_user_settings() only accepts these four
// top-level, "_settings"-suffixed keys (see allowed_keys in
// app/features/users/routes.py), while the GET response nests the same
// data one level down under un-suffixed keys (appearance, privacy,
// notifications). Sending UserSettingsData's own shape back to PUT gets
// silently filtered out server-side ("No valid settings provided").
export interface UpdateUserSettingsRequest {
  email_preferences?: Partial<{ marketing_emails: boolean; product_updates: boolean; security_alerts: boolean; newsletter: boolean }>;
  privacy_settings?: Partial<{ public_profile: boolean; activity_tracking: boolean; data_collection: boolean }>;
  appearance_settings?: Partial<{ theme: string; variant: string; language: string; timezone: string }>;
  notification_settings?: Partial<{ email_notifications: boolean; push_notifications: boolean; sms_notifications: boolean }>;
}

// User activity data type
export interface UserActivityData {
  activities: Array<{
    id: number;
    activity_type: string;
    description: string;
    created_at: string;
    ip_address: string;
    user_agent: string;
    metadata?: Record<string, unknown>;
  }>;
  total: number;
  has_more: boolean;
}

// User activity response type
export type UserActivityResponse = StandardResponse<UserActivityData>;

// User metadata data type
export interface UserMetadataData {
  metadata: {
    onboarding_completed: boolean;
    preferences: {
      dashboard_layout: string;
      auto_save: boolean;
      theme_preference?: string;
    };
    subscription?: {
      plan: string;
      expires: string;
      status: string;
    };
    analytics?: {
      last_login: string;
      login_count: number;
    };
  };
}

// User metadata response type
export type UserMetadataResponse = StandardResponse<UserMetadataData>;

export interface LoginRequest {
  email?: string; // Either email OR username is required
  username?: string; // Either email OR username is required
  password: string;
}

export interface RegisterRequest {
  email: string;
  username: string;
  password: string;
  first_name?: string;
  last_name?: string;
}

export interface UpdateUserRequest {
  first_name?: string;
  last_name?: string;
  data?: Record<string, unknown>;
}

export class UserApiService {
  // apiService.post/get already returns ApiResponse<T>, where T is what's in the data field
  // Backend returns { success, data: T, message }, so we just need T, not StandardResponse<T>
  async login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    return await apiService.post<LoginResponse>(
      API_ENDPOINTS.AUTH.LOGIN, 
      credentials
    );
  }

  async register(userData: RegisterRequest): Promise<ApiResponse<RegistrationResponse>> {
    return await apiService.post<RegistrationResponse>(
      API_ENDPOINTS.AUTH.REGISTER, 
      userData
    );
  }

  async getCurrentUser(): Promise<ApiResponse<UserProfileData>> {
    return await apiService.get<UserProfileData>(API_ENDPOINTS.USERS.FETCH);
  }

  async logout(): Promise<ApiResponse<void>> {
    return await apiService.post<void>(API_ENDPOINTS.AUTH.LOGOUT);
  }

  async updateUser(userData: UpdateUserRequest): Promise<ApiResponse<UpdateUserData>> {
    // Use /users/me endpoint to update current user (matches backend: PUT /users/me)
    return await apiService.put<UpdateUserData>(
      API_ENDPOINTS.USERS.ME, 
      userData
    );
  }

  async getUserSettings(): Promise<ApiResponse<UserSettingsData>> {
    return await apiService.get<UserSettingsData>(API_ENDPOINTS.USERS.SETTINGS);
  }

  async updateUserSettings(settings: UpdateUserSettingsRequest): Promise<ApiResponse<void>> {
    return await apiService.put<void>(
      API_ENDPOINTS.USERS.SETTINGS,
      settings
    );
  }

  async getUserActivity(params?: {
    limit?: number;
    offset?: number;
    activity_type?: string;
  }): Promise<ApiResponse<UserActivityData>> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    if (params?.activity_type) queryParams.append('activity_type', params.activity_type);

    const url = `${API_ENDPOINTS.USERS.ACTIVITY}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return await apiService.get<UserActivityData>(url);
  }

  async getUserMetadata(): Promise<ApiResponse<UserMetadataData>> {
    return await apiService.get<UserMetadataData>(API_ENDPOINTS.USERS.METADATA);
  }

  async changePassword(passwordData: ChangePasswordRequest): Promise<ApiResponse<void>> {
    return await apiService.post<void>(
      API_ENDPOINTS.AUTH.CHANGE_PASSWORD,
      passwordData
    );
  }

  async forgotPassword(email: string): Promise<ApiResponse<void>> {
    return await apiService.post<void>(
      API_ENDPOINTS.AUTH.FORGOT_PASSWORD,
      { email }
    );
  }

  async resetPassword(token: string, newPassword: string): Promise<ApiResponse<void>> {
    return await apiService.post<void>(
      API_ENDPOINTS.AUTH.RESET_PASSWORD,
      { token, new_password: newPassword }
    );
  }

  async updateUserMetadata(metadata: Partial<UserMetadataData['metadata']>): Promise<ApiResponse<void>> {
    return await apiService.put<void>(
      API_ENDPOINTS.USERS.METADATA,
      metadata
    );
  }

  async deleteAccount(): Promise<ApiResponse<void>> {
    // Delete current user's account via DELETE /users/me
    return await apiService.delete<void>(API_ENDPOINTS.USERS.ME);
  }
}

// Export singleton instance
export const userApiService = new UserApiService();
